import type { QuickSearchCategoryPage, QuickSearchResult } from '@/types/searchTypes'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { GRID_COLUMNS_MAP } from '@/components/QuickSearch/constants'
import { CardSection, CategorySection, FamilyCardsSection, ListSection } from '@/components/QuickSearch/Parts'
import { NoResults } from '@/components/QuickSearch/Parts/NoResults'
import { useQuickSearchLabels } from '@/components/QuickSearch/quickSearchLabels'

interface SingleCategoryViewProps {
  query: string
  categoryData: QuickSearchCategoryPage
  onResultSelect: (result: QuickSearchResult) => void
}

export function SingleCategoryView({ query, categoryData, onResultSelect }: Readonly<SingleCategoryViewProps>) {
  const { t } = useTranslation()
  const { SECTION_ICONS, SECTION_TITLES } = useQuickSearchLabels()

  const { category, results, total } = categoryData
  const isHierarchyCategory = category === 'subdepartments' || category === 'families'

  const sectionTitle = isHierarchyCategory
    ? `${t('quick-search.hierarchy')} (${total})`
    : `${SECTION_TITLES[category]} (${total})`

  const sectionSubtitle = isHierarchyCategory ? SECTION_TITLES[category] : undefined

  const categoryContent = useMemo(() => {
    switch (categoryData.category) {
      case 'subdepartments':
        return (
          <ListSection
            query={query}
            results={categoryData.results}
            onResultSelect={onResultSelect}
          />
        )
      case 'families':
        return (
          <FamilyCardsSection
            query={query}
            results={categoryData.results}
            onResultSelect={onResultSelect}
            responsiveGrid
          />
        )
      case 'supermodels':
      case 'models':
        return (
          <CardSection
            query={query}
            results={categoryData.results}
            onResultSelect={onResultSelect}
            gridColumns={GRID_COLUMNS_MAP.FULL_PAGE}
            responsiveGrid
          />
        )
      default:
        return null
    }
  }, [categoryData, query, onResultSelect])

  if (results.length === 0) {
    return <NoResults />
  }

  return (
    <div className="quick-search-results-page__content">
      <CategorySection
        title={sectionTitle}
        iconName={SECTION_ICONS[category]}
      >
        {sectionSubtitle && <p className="quick-search__section-subtitle">{sectionSubtitle}</p>}
        {categoryContent}
      </CategorySection>
    </div>
  )
}
